/**
 * Ensamblado de la aplicación.
 *
 * Una máquina de estados pequeña y explícita, sin router: las pantallas de
 * cuenta son pocas y no tienen URL propia salvo las dos a las que llegan los
 * enlaces de los correos (`/verificar-email`, `/recuperar-contrasena`), que se
 * leen UNA vez al arrancar y se borran de la barra de direcciones
 * (`enlaces/enlace-de-correo.ts`). Meter un router ahora sería arquitectura
 * especulativa (`Master §8`).
 *
 * `ADR-0004`: sin conexión la aplicación ABRE (el shell está cacheado), pero
 * no finge tener datos. El estado de conexión es visible, no se deduce.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "./api/cliente";
import { codigoDelEnlace, recordarAsesoria } from "./enlaces/asesoria-del-enlace";
import { recordarInvitacion, tokenDelEnlace } from "./enlaces/invitacion-de-cliente";
import { CAMINO, leerRuta, PROTEGIDAS, SE_LIMPIA, type RutaConocida } from "./enlaces/destino";
import { leerEnlaceDeCorreo, olvidarEnlace } from "./enlaces/enlace-de-correo";
import { origenDelEnlace } from "./enlaces/origen-de-alta";
import { olvidarPlanElegido, planDelEnlace, planElegidoPendiente, recordarPlanElegido } from "./enlaces/plan-elegido";
import { t } from "./i18n/es";
import { PantallaAcercaDe } from "./pantallas/PantallaAcercaDe";
import { PantallaComoAutorizar } from "./pantallas/PantallaComoAutorizar";
import { PantallaInvitacion } from "./pantallas/PantallaInvitacion";
import { PantallaInstalar } from "./pantallas/PantallaInstalar";
import { PantallaLogin } from "./pantallas/PantallaLogin";
import { PantallaOperador } from "./pantallas/PantallaOperador";
import { PantallaMfa } from "./pantallas/PantallaMfa";
import { PantallaNuevaContrasena } from "./pantallas/PantallaNuevaContrasena";
import { PantallaRecuperar } from "./pantallas/PantallaRecuperar";
import { PantallaRegistro } from "./pantallas/PantallaRegistro";
import { Panel } from "./pantallas/Panel";
import { PantallaSesion, type EstadoSesion } from "./pantallas/PantallaSesion";
import { PantallaSinOrganizacion } from "./pantallas/PantallaSinOrganizacion";
import { PantallaSinAcceso } from "./pantallas/PantallaSinAcceso";
import { PantallaVerificarEmail } from "./pantallas/PantallaVerificarEmail";
import { Aviso } from "./ui/primitivas";

type Vista =
  | { readonly tipo: "comprobando" }
  | { readonly tipo: "declaracion" }
  | { readonly tipo: "como-autorizar" }
  | { readonly tipo: "instalar" }
  | { readonly tipo: "operador" }
  | { readonly tipo: "anonimo"; readonly exito: string | null }
  /** `email`: llega desde «Reenviar enlace» del login, ya escrito. */
  | { readonly tipo: "registro"; readonly email?: string }
  | { readonly tipo: "recuperar" }
  | { readonly tipo: "verificar-email"; readonly token: string | null }
  | { readonly tipo: "recuperar-contrasena"; readonly token: string | null }
  | { readonly tipo: "invitacion"; readonly token: string | null }
  | { readonly tipo: "mfa"; readonly challenge: string }
  | { readonly tipo: "sin-organizacion" }
  | { readonly tipo: "sin-acceso"; readonly mensaje: string; readonly accion: string | null }
  | { readonly tipo: "sesion"; readonly estado: EstadoSesion };

const ANONIMO: Vista = { tipo: "anonimo", exito: null };

/** Los 403 de /me de quien SÍ pertenece a una empresa pero no puede entrar en la activa. */
const SIN_ACCESO: ReadonlySet<string> = new Set(["MEMBERSHIP_DEACTIVATED_BY_PLAN", "ORGANIZATION_NOT_OPERATIONAL"]);

/**
 * Tras cambiar de empresa se vuelve a cargar TODO desde la raíz: lo que hay en
 * pantalla es de la anterior, y la ruta con la que se entró (`/plan`, la vuelta
 * de un pago) también.
 */
function entrarEnLaOtraEmpresa(): void {
  window.location.assign("/");
}

/*
  La ruta se lee UNA vez, aqui, y ANTES de tocar la barra de direcciones. El
  orden no es estetico: mas abajo hay un `replaceState` que la deja en `/`, y
  cualquier cosa que quiera saber a donde se pedia ir tiene que haberlo
  preguntado antes. Leer despues de limpiar devuelve siempre la raiz, y es un
  fallo que no se ve: funciona, y manda a todo el mundo al mismo sitio.

  `leerRuta` normaliza -`/interno/`, `/Interno` y `/%69nterno` son la misma
  ruta- y nada que no este en su lista cerrada abre nada
  (`enlaces/destino.ts`).
*/
const RUTA_PEDIDA: RutaConocida | null = leerRuta(window.location.pathname);

/**
 * La pantalla con sesion a la que se iba, si se iba a alguna.
 *
 * Se usa DESPUES de entrar, para no dejar en el panel general, preguntandose
 * que ha pasado con su enlace, a alguien que pidio `/interno`. No autoriza
 * nada: ver `enlaces/destino.ts`.
 */
const DESTINO: RutaConocida | null =
  RUTA_PEDIDA !== null && PROTEGIDAS.has(RUTA_PEDIDA) ? RUTA_PEDIDA : null;

const VUELTA_DE_PAGO: "confirmando" | "cancelado" | null = (() => {
  if (RUTA_PEDIDA !== "plan") return null;
  const p = new URLSearchParams(window.location.search).get("pago");
  return p === "confirmando" || p === "cancelado" ? p : null;
})();

/*
  EL PLAN ELEGIDO EN faktelio.com (`enlaces/plan-elegido.ts`). Se lee aqui,
  ANTES de limpiar la barra, por el mismo motivo que la vuelta de pago.

  Se recuerda porque el camino de quien no tiene cuenta pasa por el enlace de
  confirmacion del correo, que abre OTRA pestana: sin recordarlo, el plan se
  perderia justo antes de llegar al pago. Y quien entra por `/registro` -el
  boton de Free- ha elegido Free, asi que un plan de pago recordado de antes
  se olvida.

  No concede nada: dice que boton ensenar primero. El pago sigue pidiendo
  sesion de OWNER y datos fiscales, en la API.
*/
const PLAN_DEL_ENLACE = RUTA_PEDIDA === "plan" ? planDelEnlace(window.location.search) : null;
if (PLAN_DEL_ENLACE !== null) recordarPlanElegido(PLAN_DEL_ENLACE, Date.now());
if (RUTA_PEDIDA === "registro") olvidarPlanElegido();
const PLAN_ELEGIDO = PLAN_DEL_ENLACE ?? planElegidoPendiente(Date.now());

/*
  DE QUE PAGINA DEL SITIO VIENE (`enlaces/origen-de-alta.ts`). Tambien ANTES de
  limpiar la barra, y solo en las dos puertas que enlaza el sitio. Viaja con el
  alta y no se guarda en ningun sitio.
*/
const ORIGEN_DEL_ENLACE =
  RUTA_PEDIDA === "registro" || RUTA_PEDIDA === "plan" ? origenDelEnlace(window.location.search) : null;

/*
  EL CODIGO DE UNA GESTORIA (`enlaces/asesoria-del-enlace.ts`, OD-0063 D-08).
  Tambien ANTES de limpiar la barra, y solo en `/registro`, que es el enlace
  que reparte la gestoria. Se RECUERDA, como el plan elegido, porque el correo
  de confirmacion abre otra pestana, y la que crea la empresa lo lee de ahi.
  No da acceso a nada: lo manda el alta, y la API decide si atribuye.
*/
const ASESORIA_DEL_ENLACE = RUTA_PEDIDA === "registro" ? codigoDelEnlace(window.location.search) : null;
if (ASESORIA_DEL_ENLACE !== null) recordarAsesoria(ASESORIA_DEL_ENLACE, Date.now());

/*
  LA INVITACION DE UN CLIENTE A SU GESTORIA (`enlaces/invitacion-de-cliente.ts`,
  OD-0063 D-04, enmienda del 2026-10-28). Igual que el codigo de arriba: antes de
  limpiar la barra, solo en `/registro`, y recordada para la pestana que tenga
  la empresa. No da acceso a nada: el Inicio la manda a la API.
*/
const INVITACION_DEL_ENLACE = RUTA_PEDIDA === "registro" ? tokenDelEnlace(window.location.search) : null;
if (INVITACION_DEL_ENLACE !== null) recordarInvitacion(INVITACION_DEL_ENLACE, Date.now());

/*
  La barra se limpia al cargar, igual que con los enlaces del correo. CUALES y
  por que, en `enlaces/destino.ts`; aqui solo se obedece, y DESPUES de haber
  leido la ruta arriba.
*/
if (RUTA_PEDIDA !== null && SE_LIMPIA.has(RUTA_PEDIDA)) window.history.replaceState(null, "", "/");

/**
 * La vista inicial sale de la ruta, o del enlace del correo si se ha llegado
 * por uno.
 *
 * Se calcula al CARGAR EL MÓDULO, una sola vez, y el enlace se borra de la
 * barra en ese mismo momento, antes de pintar nada. No va en un inicializador
 * de `useState`: React puede llamarlo dos veces, y la segunda ya vería la
 * barra limpia.
 */
const VISTA_INICIAL: Vista = (() => {
  if (RUTA_PEDIDA === "registro") return { tipo: "registro" };
  if (RUTA_PEDIDA === "declaracion") return { tipo: "declaracion" };
  if (RUTA_PEDIDA === "como-autorizar") return { tipo: "como-autorizar" };
  if (RUTA_PEDIDA === "instalar") return { tipo: "instalar" };

  /*
    `/interno` y `/entrar` arrancan COMPROBANDO, no en su pantalla, y por eso
    no aparecen aqui.

    Antes `/interno` pintaba el panel de operador directamente. Sin sesion eso
    daba una pantalla vacia y un "no se ha encontrado" -la API contesta 404 a
    quien no es operador, y a quien no ha entrado tambien-, asi que el operador
    que abria su marcador no leia "entra": leia que su herramienta no existia.
    Ahora se pregunta por la sesion primero y, si no hay, se ensena la entrada y
    se vuelve aqui al terminar (`DESTINO`).
  */
  const enlace = leerEnlaceDeCorreo(window.location.pathname, window.location.hash);
  if (enlace === null) return { tipo: "comprobando" };
  olvidarEnlace();
  if (enlace.tipo === "verificar-email-roto") return { tipo: "verificar-email", token: null };
  if (enlace.tipo === "recuperar-contrasena-roto") return { tipo: "recuperar-contrasena", token: null };
  if (enlace.tipo === "invitacion-roto") return { tipo: "invitacion", token: null };
  return { tipo: enlace.tipo, token: enlace.token };
})();

function useConexion(): boolean {
  const [hayConexion, setHayConexion] = useState(() => navigator.onLine);
  useEffect(() => {
    const arriba = (): void => setHayConexion(true);
    const abajo = (): void => setHayConexion(false);
    window.addEventListener("online", arriba);
    window.addEventListener("offline", abajo);
    return () => {
      window.removeEventListener("online", arriba);
      window.removeEventListener("offline", abajo);
    };
  }, []);
  return hayConexion;
}

/**
 * El ancho depende de lo que se enseña, no del gusto:
 *
 * ```text
 * formularios   34rem  una columna estrecha, que es como mejor se rellenan
 * panel         todo   rejillas que crecen en columnas (propietario 2026-09-21)
 * guia          58rem  lleva dibujos de 520px de ancho; a 34rem salian
 *                      diminutos y el texto se partia cada cinco palabras
 * ```
 */
function claseDePagina(vista: Vista): string {
  if (vista.tipo === "sesion" && vista.estado.tipo === "datos") return "pagina pagina-ancha";
  if (vista.tipo === "como-autorizar") return "pagina pagina-guia";
  return "pagina";
}

export function App() {
  const [vista, setVista] = useState<Vista>(VISTA_INICIAL);
  const hayConexion = useConexion();

  /*
    Se gasta UNA vez. Si se quedara puesto, cerrar sesion y volver a entrar
    devolveria al panel de operador a quien acaba de pedir salir de ahi.
  */
  const destino = useRef<RutaConocida | null>(DESTINO);

  /*
    OD-0060 D-04: la invitacion que se estaba aceptando cuando hizo falta
    entrar. Se vuelve a ella al terminar de entrar, y se gasta UNA vez: no se
    guarda en el dispositivo, solo vive en esta pestana (ADR-0004).
  */
  const invitacion = useRef<string | null>(null);

  const cargarSesion = useCallback(async (): Promise<void> => {
    if (invitacion.current !== null) {
      const token = invitacion.current;
      invitacion.current = null;
      setVista({ tipo: "invitacion", token });
      return;
    }
    setVista({ tipo: "sesion", estado: { tipo: "cargando" } });
    const r = await api.sesion();

    /*
      Un 401 es lo UNICO que significa con certeza "no has entrado", y es lo
      unico que manda a la entrada. Con CUALQUIER otra respuesta se sigue al
      destino, y se puede porque esa pantalla no concede nada: lo que decide
      quien es operador es la API en cada peticion.

      Importa para dos casos que si no se romperian. Un operador interno puede
      no pertenecer a ninguna organizacion -403-, y mandarle a "te falta una
      organizacion" en vez de a su herramienta seria romper justo el caso para
      el que existe. Y si /me esta caido -500-, la herramienta con la que se
      diagnostica una caida es precisamente la que no debe desaparecer.
    */
    const sinSesion = !r.ok && r.estado === 401;
    if (!sinSesion && destino.current === "operador") {
      destino.current = null;
      setVista({ tipo: "operador" });
      return;
    }
    if (r.ok) {
      setVista({ tipo: "sesion", estado: { tipo: "datos", sesion: r.datos } });
      return;
    }
    if (sinSesion) {
      // El destino se CONSERVA: se entra, y despues se va alli.
      //
      // Quien llega desde un plan de pago del sitio y no tiene sesion casi
      // siempre viene a darse de alta: se le ensena el alta, con su plan
      // arriba, y «Ya tengo cuenta» sigue a un clic.
      setVista(PLAN_DEL_ENLACE !== null ? { tipo: "registro" } : ANONIMO);
      return;
    }
    if (r.estado === 403) {
      // /me no pide capability. Sus 403 (auth-tenancy.ts): no pertenecer a
      // ninguna organización, o no poder entrar en la activa —cerrada, o el
      // acceso desactivado al pasar a Free—, que traen su motivo.
      if (r.error !== null && SIN_ACCESO.has(r.error.motivo ?? "")) {
        setVista({ tipo: "sin-acceso", mensaje: r.error.message, accion: r.error.action });
        return;
      }
      setVista({ tipo: "sin-organizacion" });
      return;
    }
    if (r.estado === 429) {
      setVista({ tipo: "sesion", estado: { tipo: "limite" } });
      return;
    }
    setVista({ tipo: "sesion", estado: { tipo: "error" } });
  }, []);

  useEffect(() => {
    // Sólo se comprueba la sesión si NO se ha llegado por un enlace de correo:
    // esa pantalla no debe desaparecer bajo los pies de quien la está leyendo.
    if (VISTA_INICIAL.tipo === "comprobando") void cargarSesion();
  }, [cargarSesion]);

  return (
    <div className={claseDePagina(vista)}>
      <a className="salto-a-contenido" href="#contenido">
        {t.app.saltarAContenido}
      </a>
      {/*
        Con sesion, la marca y el aviso de conexion van en el propio panel.

        Y LA MARCA VA TAMBIEN AQUI. Hasta el 2026-10-15 estas pantallas -entrar,
        crear cuenta, recuperar, la guia- ponian «Facturacion» mientras que el
        panel, YA DENTRO, ponia «Faktelio» (Panel.tsx:304). Estaba al reves: la
        marca importa justo donde el usuario acaba de llegar desde
        faktelio.com, y sobra donde ya sabe donde esta. La clave `app.marca`
        existia desde entonces y no la usaba nadie fuera del panel.
      */}
      {vista.tipo === "sesion" && vista.estado.tipo === "datos" ? null : (
        <header className="cabecera">
          <h1 className="marca">{t.app.marca}</h1>
          {!hayConexion ? <span className="entorno">{t.conexion.sinConexion}</span> : null}
        </header>
      )}

      <main id="contenido">
        {!hayConexion ? (
          <Aviso tono="aviso" titulo={t.conexion.sinConexion}>
            {t.conexion.sinConexionDetalle}
          </Aviso>
        ) : null}

        {vista.tipo === "comprobando" ? (
          <Aviso tono="info" titulo={t.comprobando.titulo}>
            {t.comprobando.detalle}
          </Aviso>
        ) : null}

        {vista.tipo === "declaracion" ? <PantallaAcercaDe /> : null}
        {vista.tipo === "como-autorizar" ? <PantallaComoAutorizar /> : null}
        {vista.tipo === "instalar" ? (
          <PantallaInstalar onRegistro={() => setVista({ tipo: "registro" })} onEntrar={() => void cargarSesion()} />
        ) : null}

        {vista.tipo === "operador" ? <PantallaOperador /> : null}

        {vista.tipo === "anonimo" ? (
          <PantallaLogin
            hayConexion={hayConexion}
            exito={vista.exito}
            onAutenticado={() => void cargarSesion()}
            onMfa={(challenge) => setVista({ tipo: "mfa", challenge })}
            onRegistro={() => setVista({ tipo: "registro" })}
            onRecuperar={() => setVista({ tipo: "recuperar" })}
            onReenviarEnlace={(email) => setVista({ tipo: "registro", email })}
          />
        ) : null}

        {vista.tipo === "anonimo" ? (
          <p className="ayuda-neutra">
            <a href={CAMINO.declaracion}>{t.acercaDe.enlacePublico}</a>
          </p>
        ) : null}

        {vista.tipo === "registro" ? (
          <PantallaRegistro
            hayConexion={hayConexion}
            planElegido={PLAN_ELEGIDO}
            origen={ORIGEN_DEL_ENLACE}
            deAsesoria={ASESORIA_DEL_ENLACE !== null}
            emailInicial={vista.email ?? null}
            onVolver={() => setVista(ANONIMO)}
          />
        ) : null}

        {vista.tipo === "recuperar" ? (
          <PantallaRecuperar hayConexion={hayConexion} onVolver={() => setVista(ANONIMO)} />
        ) : null}

        {vista.tipo === "verificar-email" ? (
          <PantallaVerificarEmail
            token={vista.token}
            hayConexion={hayConexion}
            onVerificado={() => setVista({ tipo: "anonimo", exito: t.login.emailVerificado })}
            onPedirOtro={() => setVista({ tipo: "registro" })}
            onVolver={() => setVista(ANONIMO)}
          />
        ) : null}

        {vista.tipo === "recuperar-contrasena" ? (
          <PantallaNuevaContrasena
            token={vista.token}
            hayConexion={hayConexion}
            onCambiada={() => setVista({ tipo: "anonimo", exito: t.login.passwordCambiada })}
            onPedirOtro={() => setVista({ tipo: "recuperar" })}
            onVolver={() => setVista(ANONIMO)}
          />
        ) : null}

        {vista.tipo === "invitacion" ? (
          <PantallaInvitacion
            token={vista.token}
            hayConexion={hayConexion}
            // Dentro: la API ya ha dejado la sesion en esa empresa.
            onAceptada={() => void cargarSesion()}
            onEntrar={() => {
              invitacion.current = vista.token;
              setVista(ANONIMO);
            }}
            onRegistro={() => setVista({ tipo: "registro" })}
            onContinuar={() => void cargarSesion()}
          />
        ) : null}

        {vista.tipo === "mfa" ? (
          <PantallaMfa
            challenge={vista.challenge}
            hayConexion={hayConexion}
            onAutenticado={() => void cargarSesion()}
            onVolver={() => setVista(ANONIMO)}
          />
        ) : null}

        {vista.tipo === "sin-organizacion" ? (
          <PantallaSinOrganizacion
            hayConexion={hayConexion}
            onCreada={() => void cargarSesion()}
            onSesionCaducada={() => setVista(ANONIMO)}
            onSalir={() => setVista(ANONIMO)}
          />
        ) : null}

        {vista.tipo === "sin-acceso" ? (
          <PantallaSinAcceso
            mensaje={vista.mensaje}
            accion={vista.accion}
            onCambiada={entrarEnLaOtraEmpresa}
            onSalir={() => setVista(ANONIMO)}
          />
        ) : null}

        {vista.tipo === "sesion" && vista.estado.tipo === "datos" ? (
          <Panel
            sesion={vista.estado.sesion}
            hayConexion={hayConexion}
            onSalir={() => setVista(ANONIMO)}
            abrirPlan={RUTA_PEDIDA === "plan" || PLAN_ELEGIDO !== null}
            planElegido={PLAN_ELEGIDO}
            vueltaDePago={VUELTA_DE_PAGO}
            onCambioDeEmpresa={entrarEnLaOtraEmpresa}
          />
        ) : null}

        {vista.tipo === "sesion" && vista.estado.tipo !== "datos" ? (
          <PantallaSesion
            estado={vista.estado}
            onRecargar={() => void cargarSesion()}
            onSalir={() => setVista(ANONIMO)}
          />
        ) : null}
      </main>
      {/*
        AQUI HABIA UN PIE que decia, en TODAS las pantallas y sin mirar el
        entorno, «Versión en construcción: todavía no se pueden emitir
        facturas». Era verdad hasta el 2026-10-13 y dejó de serlo: GATE-05 está
        en PASS, el certificado mTLS está enlazado y producción remite a la AEAT
        de verdad. Lo leía un cliente justo antes de escribir su contraseña.

        No se sustituye por la frase contraria: un pie que afirme que SÍ se
        puede emitir volvería a ser una afirmación que caduca sola. Lo que
        decida contarse aquí tendrá que salir de algo que el servidor sepa.
      */}
    </div>
  );
}
